import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/apiRouting';

interface StoreEditFormProps {
  store: any;
  onSubmit: (formData: any) => Promise<void>;
  onCancel: () => void;
  enterprises: any[];
}

const StoreEditForm: React.FC<StoreEditFormProps> = ({ store, onSubmit, onCancel, enterprises }) => {
  const [formData, setFormData] = useState({
    name: store?.name || '',
    conceptId: store?.conceptId || '',
    franchiseeId: store?.franchiseeId || '',
    address: typeof store?.address === 'string' ? store.address : 
             (store?.address ? `${store.address.street || ''}, ${store.address.city || ''}, ${store.address.state || ''} ${store.address.zipCode || ''}`.trim() : ''),
    phone: store?.phone || '',
    email: store?.email || '',
    storeType: store?.storeType || store?.type || 'company_owned',
    enterpriseId: store?.enterpriseId || '',
    status: store?.status || 'active',
    generateNewStoreCode: false,
    latitude: store?.latitude || '',
    longitude: store?.longitude || '',
  });

  const [concepts, setConcepts] = useState<any[]>([]);
  const [franchisees, setFranchisees] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStoreCode, setCurrentStoreCode] = useState(store?.storeCode || 'Not assigned');
  const [geocoding, setGeocoding] = useState(false);
  const [geocodingMessage, setGeocodingMessage] = useState('');
  
  // Debug logging
  console.log('StoreEditForm initialized with store:', store);
  console.log('Store code found:', store?.storeCode);
  console.log('Store ID found:', store?.storeId || store?.id);

  const fetchConceptsAndFranchisees = async (enterpriseId: string) => {
    if (!enterpriseId) {
      setConcepts([]);
      setFranchisees([]);
      return;
    }

    try {
      setLoading(true);
      
      // Fetch concepts
      const conceptsResponse = await fetch(API_ENDPOINTS.concepts(enterpriseId));
      if (conceptsResponse.ok) {
        const conceptsData = await conceptsResponse.json();
        setConcepts(conceptsData.data || []);
      }

      // Fetch franchisees
      const franchiseesResponse = await fetch(API_ENDPOINTS.franchisees(enterpriseId));
      if (franchiseesResponse.ok) {
        const franchiseesData = await franchiseesResponse.json();
        setFranchisees(franchiseesData.data || []);
      }
    } catch (error) {
      console.error('Error fetching concepts and franchisees:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formData.enterpriseId) {
      fetchConceptsAndFranchisees(formData.enterpriseId);
    }
  }, [formData.enterpriseId]);

  // Load initial data
  useEffect(() => {
    if (store?.enterpriseId) {
      fetchConceptsAndFranchisees(store.enterpriseId);
    }
  }, [store]);

  const handleChange = (field: string, value: string | boolean) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Reset concept and franchisee when enterprise changes
      if (field === 'enterpriseId') {
        newData.conceptId = '';
        newData.franchiseeId = '';
      }
      
      return newData;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.enterpriseId || !formData.conceptId || !formData.address.trim()) {
      alert('Store name, enterprise, concept, and address are required');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        ...formData,
        type: 'store',
        storeId: store.storeId || store.id, // Include store ID for update
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatAddress = (address: string | { street?: string; city?: string; state?: string; zipCode?: string; country?: string; }): string => {
    if (typeof address === 'string') {
      return address;
    }
    
    if (typeof address === 'object' && address !== null) {
      const parts = [];
      if (address.street) parts.push(address.street);
      if (address.city) parts.push(address.city);
      if (address.state) parts.push(address.state);
      if (address.zipCode) parts.push(address.zipCode);
      return parts.join(', ') || 'No address provided';
    }
    
    return 'No address provided';
  };

  const generateNewStoreCodePreview = () => {
    if (!formData.name.trim()) return '';
    
    const baseCode = formData.name
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .substring(0, 3)
      .padEnd(3, 'X');
    
    return `${baseCode}XXX (example - actual number will be assigned)`;
  };

  const handleGeocode = async () => {
    if (!formData.address.trim()) {
      alert('Please enter an address first');
      return;
    }

    setGeocoding(true);
    setGeocodingMessage('Geocoding address...');

    try {
      // Use OpenStreetMap Nominatim for geocoding as a fallback
      const encodedAddress = encodeURIComponent(formData.address);
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}&limit=1`, {
        headers: {
          'User-Agent': 'BigSystemOne/1.0'
        }
      });

      if (!response.ok) {
        throw new Error(`Geocoding failed: ${response.status}`);
      }

      const data = await response.json();
      
      if (data && data.length > 0 && data[0].lat && data[0].lon) {
        const result = data[0];
        setFormData(prev => ({
          ...prev,
          latitude: result.lat,
          longitude: result.lon
        }));
        setGeocodingMessage(`Successfully geocoded: ${result.lat}, ${result.lon}`); 
      } else {
        throw new Error('No coordinates found for this address');
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setGeocodingMessage(`Geocoding failed: ${errorMessage}`);
    } finally {
      setGeocoding(false);
      // Clear message after 5 seconds
      setTimeout(() => setGeocodingMessage(''), 5000);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Store Information Display */}
      <div className="form-group" style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '14px' }}>
          <div>
            <strong>Store ID:</strong> {store?.storeId || store?.id || 'N/A'}
          </div>
          <div>
            <strong>Current POS Code:</strong> 
            <span style={{ 
              color: currentStoreCode === 'Not assigned' ? '#dc3545' : '#28a745',
              fontWeight: '600',
              marginLeft: '8px'
            }}>
              {currentStoreCode}
            </span>
          </div>
          <div>
            <strong>Created:</strong> {store?.createdAt ? new Date(store.createdAt).toLocaleDateString() : 'N/A'}
          </div>
          <div>
            <strong>Last Updated:</strong> {store?.updatedAt ? new Date(store.updatedAt).toLocaleDateString() : 'N/A'}
          </div>
        </div>
      </div>

      <div className="form-group">
        <label className="label-modern">Enterprise *</label>
        <select
          className="input-modern"
          value={formData.enterpriseId}
          onChange={(e) => handleChange('enterpriseId', e.target.value)}
          required
        >
          <option value="">Select Enterprise</option>
          {enterprises.map((enterprise) => (
            <option key={enterprise.enterpriseId} value={enterprise.enterpriseId}>
              {enterprise.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="label-modern">Concept *</label>
        <select
          className="input-modern"
          value={formData.conceptId}
          onChange={(e) => handleChange('conceptId', e.target.value)}
          required
          disabled={!formData.enterpriseId || loading}
        >
          <option value="">Select Concept</option>
          {concepts.map((concept) => (
            <option key={concept.conceptId} value={concept.conceptId}>
              {concept.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="label-modern">Store Type *</label>
        <select
          className="input-modern"
          value={formData.storeType}
          onChange={(e) => handleChange('storeType', e.target.value)}
          required
        >
          <option value="company_owned">Company Owned</option>
          <option value="franchised">Franchised</option>
        </select>
      </div>

      {formData.storeType === 'franchised' && (
        <div className="form-group">
          <label className="label-modern">Franchisee</label>
          <select
            className="input-modern"
            value={formData.franchiseeId}
            onChange={(e) => handleChange('franchiseeId', e.target.value)}
            disabled={!formData.enterpriseId || loading}
          >
            <option value="">Select Franchisee (Optional)</option>
            {franchisees.map((franchisee) => (
              <option key={franchisee.franchiseeId} value={franchisee.franchiseeId}>
                {franchisee.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="form-group">
        <label className="label-modern">Store Name *</label>
        <input
          type="text"
          className="input-modern"
          placeholder="Enter store name"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          required
        />
      </div>

      <div className="form-group">
        <label className="label-modern">Address *</label>
        <textarea
          className="input-modern"
          placeholder="Enter store address"
          rows={3}
          value={formData.address}
          onChange={(e) => handleChange('address', e.target.value)}
          required
        />
        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            type="button"
            className="btn-modern btn-modern-secondary"
            onClick={handleGeocode}
            disabled={geocoding || !formData.address.trim()}
            style={{ fontSize: '12px', padding: '6px 12px' }}
          >
            {geocoding ? 'Geocoding...' : 'Geocode Now'}
          </button>
          {geocodingMessage && (
            <span style={{ 
              fontSize: '12px', 
              color: geocodingMessage.includes('Successfully') ? '#28a745' : '#dc3545',
              fontWeight: '500'
            }}>
              {geocodingMessage}
            </span>
          )}
        </div>
      </div>

      {/* Coordinates Display/Edit */}
      <div className="form-group" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <label className="label-modern">Latitude</label>
          <input
            type="number"
            step="any"
            className="input-modern"
            placeholder="e.g., 26.8153"
            value={formData.latitude}
            onChange={(e) => handleChange('latitude', e.target.value)}
          />
        </div>
        <div>
          <label className="label-modern">Longitude</label>
          <input
            type="number"
            step="any"  
            className="input-modern"
            placeholder="e.g., -80.0531"
            value={formData.longitude}
            onChange={(e) => handleChange('longitude', e.target.value)}
          />
        </div>
      </div>

      <div className="form-group">
        <label className="label-modern">Phone</label>
        <input
          type="tel"
          className="input-modern"
          placeholder="Enter phone number"
          value={formData.phone}
          onChange={(e) => handleChange('phone', e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="label-modern">Email</label>
        <input
          type="email"
          className="input-modern"
          placeholder="Enter email address"
          value={formData.email}
          onChange={(e) => handleChange('email', e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="label-modern">Status *</label>
        <select
          className="input-modern"
          value={formData.status}
          onChange={(e) => handleChange('status', e.target.value)}
          required
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="pending">Pending</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      {/* POS Store Code Section */}
      <div className="form-group" style={{ marginTop: '24px', padding: '16px', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffeaa7' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
          <input
            type="checkbox"
            id="generateNewStoreCode"
            checked={formData.generateNewStoreCode}
            onChange={(e) => {
              console.log('Checkbox changed:', e.target.checked);
              handleChange('generateNewStoreCode', e.target.checked);
            }}
            style={{ marginRight: '8px' }}
          />
          <label htmlFor="generateNewStoreCode" style={{ fontWeight: '600', color: '#856404' }}>
            Generate New POS Store Code
          </label>
        </div>
        
        <div style={{ fontSize: '14px', color: '#856404' }}>
          <p style={{ margin: '0 0 8px 0' }}>
            <strong>Current Code:</strong> {currentStoreCode}
          </p>
          {formData.generateNewStoreCode && (
            <p style={{ margin: '0', fontStyle: 'italic' }}>
              <strong>New Code Preview:</strong> {generateNewStoreCodePreview()}
            </p>
          )}
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            Debug: generateNewStoreCode = {formData.generateNewStoreCode ? 'true' : 'false'}
          </div>
          <p style={{ margin: '8px 0 0 0', fontSize: '12px' }}>
            POS devices use this code to register with the store. Generate a new code if the current one is compromised or needs to be reset.
          </p>
        </div>
      </div>

      <div className="modal-footer">
        <button
          type="button"
          className="btn-modern btn-modern-secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-modern btn-modern-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Updating...' : 'Update Store'}
        </button>
      </div>
    </form>
  );
};

export default StoreEditForm;